import { useEffect, useState } from "react";

type NavDot = { id: string; label: string };

const dots: NavDot[] = [
  { id: "start", label: "Start" },
  { id: "wystapienia", label: "Wystąpienia" },
  { id: "wprowadzenie", label: "Odpowiedzialność" },
  { id: "rezultaty", label: "Rezultaty" },
  { id: "program", label: "Mastermind" },
  { id: "oferta", label: "Oferta" },
  { id: "rekomendacje", label: "Rekomendacje uczestników" },
  { id: "o-mnie", label: "O mnie" },
  { id: "media", label: "Media" },
  { id: "faq", label: "FAQ" },
  { id: "kontakt", label: "Kontakt" },
];

/**
 * Pionowa nawigacja kropkowa (desktop) — pokazuje bieżącą sekcję strony głównej.
 * Renderuje się dopiero po hydratacji; na mobile ukryta przez klasy Tailwind.
 */
export function NavDots() {
  const [active, setActive] = useState<string>("start");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const sections = dots
      .map((d) => document.getElementById(d.id))
      .filter((el): el is HTMLElement => el !== null);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(entry.target.id);
        }
      },
      { rootMargin: "-40% 0px -55% 0px" },
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  if (!mounted) return null;

  return (
    <nav
      aria-label="Nawigacja po sekcjach strony"
      className="fixed right-5 top-1/2 z-40 hidden -translate-y-1/2 lg:block xl:right-8"
    >
      <ul className="flex flex-col items-center gap-3">
        {dots.map((dot) => {
          const isActive = active === dot.id;
          return (
            <li key={dot.id}>
              <button
                type="button"
                aria-label={`Przejdź do sekcji: ${dot.label}`}
                aria-current={isActive ? "true" : undefined}
                onClick={() => {
                  document
                    .getElementById(dot.id)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  history.replaceState(null, "", `#${dot.id}`);
                }}
                className="group relative flex h-4 w-4 items-center justify-center"
              >
                <span
                  aria-hidden="true"
                  className={`block rounded-full transition-all duration-300 ${
                    isActive
                      ? "h-2 w-2 bg-coral-text"
                      : "h-1.5 w-1.5 bg-ink/25 group-hover:bg-coral-text/60"
                  }`}
                />
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 whitespace-nowrap text-[0.62rem] font-medium uppercase tracking-[0.16em] text-ink-soft opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                >
                  {dot.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
